import React from 'react';
import { IonIcon } from '@ionic/react';
import { checkmarkCircleOutline, chevronForwardOutline, peopleOutline, removeCircleOutline } from 'ionicons/icons';
import { Virtuoso } from 'react-virtuoso';
import GridStatus from './GridStatus';

type Props = {
  items: any[];
  variant: 'device' | 'client' | 'person';
  virtualized?: boolean;
  matches?: (item: any) => boolean;
  canEdit: (item: any) => boolean;
  onEdit: (item: any) => void;
  renderActions: (item: any) => React.ReactNode;
  renderExpanded?: (item: any, originalIndex: number) => React.ReactNode;
};

const documento = (value: string) => value.replace(/\D/g, '').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2');

export default function CadastroGrid({ items, variant, virtualized, matches, canEdit, onEdit, renderActions, renderExpanded }: Props) {
  // Keep original indexes: Equipe uses them to update module permissions.
  const rows = (items || []).map((item, index) => ({ item, index })).filter(({ item }) => !matches || matches(item));
  const device = variant === 'device';
  const row = ({ item, index }: typeof rows[number]) => {
    const editable = canEdit(item);
    const active = item.statusUsuario === 'ativo';
    return <div className="cadastros-record" key={item.pk || index} role="rowgroup">
      <div role="row" className={`cadastros-row ${editable ? 'is-editable' : ''}`} tabIndex={editable ? 0 : undefined}
        aria-label={editable ? `Editar ${item.nome}` : undefined}
        onClick={() => { if (editable) onEdit(item); }}
        onKeyDown={event => {
          if (event.target === event.currentTarget && editable && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault(); onEdit(item);
          }
        }}>
        <div role="cell" className="cadastros-name"><strong>{item.nome}</strong>
          {variant === 'person' && item.perfil && <small>{item.perfil === 'empresa' || item.perfil === 'usuarioadmin' ? 'administrador' : item.perfil}</small>}
          {variant === 'person' && item.cpf && <small>{documento(item.cpf)}</small>}
        </div>
        {!device && <div role="cell" className="cadastros-contact"><span>{item.telefone || '—'}</span>{variant === 'person' && item.email && <small>{item.email}</small>}</div>}
        <div role="cell" className="cadastros-details">
          {item.endereco && !device && <span>{item.endereco}</span>}
          {variant === 'client' && item.complemento && <small>{item.complemento}</small>}
          {variant === 'person' && item.dataAniversario && <small>Aniversário: {new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC' }).format(Date.parse(item.dataAniversario))}</small>}
          {item.lojas?.filter((loja: any) => loja.empresaLojaNome !== '').map((loja: any, lojaIndex: number) => <small key={loja.empresaLojaId || lojaIndex}>{loja.empresaLojaNome}</small>)}
        </div>
        {variant !== 'client' && <div role="cell" className="cadastros-status">{item.statusUsuario && <GridStatus tone={active ? 'success' : 'neutral'} icon={active ? checkmarkCircleOutline : removeCircleOutline}>{active ? 'Ativo' : item.statusUsuario === 'inativo' ? 'Inativo' : item.statusUsuario}</GridStatus>}</div>}
        <div role="cell" className="cadastros-actions">
          <div className="cadastros-action-buttons" onClick={event => event.stopPropagation()}>{renderActions(item)}</div>
          {editable && <IonIcon className="cadastros-chevron" icon={chevronForwardOutline} aria-hidden="true" />}
        </div>
      </div>
      {renderExpanded && <div role="row"><div role="cell" className="cadastros-expanded-cell"><details className="cadastros-expanded"><summary>Módulos e permissões de {item.nome}</summary>{renderExpanded(item, index)}</details></div></div>}
    </div>;
  };
  return <div className={`cadastros-grid cadastros-grid--${variant} ${virtualized ? 'is-virtual' : ''}`} role="table" aria-label="Cadastros">
    <div role="row" className="cadastros-grid-header">
      <span role="columnheader">Nome</span>{!device && <span role="columnheader">Contato</span>}
      <span role="columnheader">{device ? 'Lojas' : 'Informações'}</span>{variant !== 'client' && <span role="columnheader">Status</span>}
      <span role="columnheader" className="cadastros-actions-heading">Ações</span>
    </div>
    {rows.length === 0 ? <div className="cadastros-empty"><IonIcon icon={peopleOutline} aria-hidden="true" /><h2>Nenhum registro encontrado</h2><p>Os cadastros que correspondem à sua pesquisa aparecerão aqui.</p></div>
      : virtualized ? <Virtuoso className="cadastros-virtual-list" data={rows} computeItemKey={(_, value) => value.item.pk || value.index} itemContent={(_, value) => row(value)} />
        : rows.map(value => row(value))}
  </div>;
}
